/**
 * Bank Account Display Component
 *
 * Shows gaushala bank account information with edit capability
 * Used in profile pages and transaction details
 */

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertCircle,
  CheckCircle,
  Clock,
  Building2,
  User,
  CreditCard,
  Mail,
  Phone,
  Edit2,
  Plus,
  Loader2,
  Eye,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  bankAccountService,
  BankAccountResponse,
} from '@/services/gaushala/bankAccountService';
import BankAccountDialog from './BankAccountDialog';

interface BankAccountDisplayProps {
  compact?: boolean;
  showEditButton?: boolean;
  onAccountUpdated?: (account: BankAccountResponse) => void;
}

export const BankAccountDisplay: React.FC<BankAccountDisplayProps> = ({
  compact = false,
  showEditButton = true,
  onAccountUpdated,
}) => {
  const [bankAccount, setBankAccount] = useState<BankAccountResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<'create' | 'edit'>('create');
  const [viewModalOpen, setViewModalOpen] = useState(false);

  /**
   * Fetch bank account on mount
   */
  useEffect(() => {
    fetchBankAccount();
  }, []);

  /**
   * Fetch bank account details
   */
  const fetchBankAccount = async () => {
    try {
      console.log('[BankAccountDisplay] Starting to fetch bank account...');
      setLoading(true);
      setError(null);
      const account = await bankAccountService.getBankAccount();
      console.log('[BankAccountDisplay] Bank account fetched successfully:', account);
      setBankAccount(account);
      setError(null);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load bank account';
      console.log('[BankAccountDisplay] Error fetching bank account:', errorMessage);
      console.log('[BankAccountDisplay] Setting error state with message:', errorMessage);
      setError(errorMessage);
      setBankAccount(null);
      console.error('Error fetching bank account:', err);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle add account click
   */
  const handleAddAccount = () => {
    setDialogMode('create');
    setDialogOpen(true);
  };

  /**
   * Handle edit account click
   */
  const handleEditAccount = () => {
    setDialogMode('edit');
    setDialogOpen(true);
  };

  /**
   * Handle view account click
   */
  const handleViewAccount = () => {
    setViewModalOpen(true);
  };

  /**
   * Handle successful save
   */
  const handleAccountSaved = (account: BankAccountResponse) => {
    setBankAccount(account);
    setDialogOpen(false);
    if (onAccountUpdated) {
      onAccountUpdated(account);
    }
  };

  /**
   * Format date
   */
  const formatDate = (dateString?: string): string => {
    if (!dateString) return '—';
    try {
      return new Date(dateString).toLocaleDateString('en-IN', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return dateString;
    }
  };

  /**
   * Loading state
   */
  if (loading) {
    return (
      <Card className={compact ? '' : ''}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            Bank Account
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-gray-500" />
          </div>
        </CardContent>
      </Card>
    );
  }

  /**
   * Error state - No bank account found
   */
  if (error && (error.toLowerCase().includes('not found') || error.toLowerCase().includes('bank account not found'))) {
    console.log('[BankAccountDisplay] Showing not found state with error:', error);
    return (
      <Card className={compact ? '' : ''}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            Bank Account
          </CardTitle>
          <CardDescription>No bank account linked</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Alert className="bg-blue-50 border-blue-200">
            <AlertCircle className="h-4 w-4 text-blue-600" />
            <AlertDescription className="text-blue-800">
              Link your bank account to enable raw material transactions and payments
            </AlertDescription>
          </Alert>
          {showEditButton && (
            <Button onClick={handleAddAccount} className="w-full gap-2">
              <Plus className="h-4 w-4" />
              Link Bank Account
            </Button>
          )}
        </CardContent>
      </Card>
    );
  }

  /**
   * Other error state
   */
  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            Bank Account
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
          {showEditButton && (
            <Button
              onClick={fetchBankAccount}
              variant="outline"
              className="mt-4 w-full"
            >
              Retry
            </Button>
          )}
        </CardContent>
      </Card>
    );
  }

  /**
   * Account details view
   */
  if (bankAccount) {
    return (
      <>
        <Card className={compact ? 'border-blue-100 bg-blue-50' : ''}>
          <CardHeader>
            <div className="flex items-start justify-between">
              <div className="space-y-1">
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="h-5 w-5" />
                  Bank Account
                </CardTitle>
                <CardDescription>
                  {bankAccount.bankName} • {bankAccount.accountType || 'Account'}
                </CardDescription>
              </div>
              <div className="flex items-center gap-2">
                {bankAccount.isVerified ? (
                  <Badge className="bg-green-100 text-green-800 gap-1">
                    <CheckCircle className="h-3 w-3" />
                    Verified
                  </Badge>
                ) : (
                  <Badge variant="secondary" className="gap-1">
                    <Clock className="h-3 w-3" />
                    Pending
                  </Badge>
                )}
              </div>
            </div>
          </CardHeader>

          <CardContent className="space-y-4">

            {/* Account Details Grid */}
            <div className="grid gap-4 md:grid-cols-2">
              {/* Account Holder */}
              <div className="space-y-1">
                <div className="flex items-center gap-2 text-sm font-medium text-gray-600">
                  <User className="h-4 w-4" />
                  Account Holder
                </div>
                <p className="text-base font-medium">{bankAccount.accountHolderName}</p>
              </div>

              {/* Account Number */}
              <div className="space-y-1">
                <div className="flex items-center gap-2 text-sm font-medium text-gray-600">
                  <CreditCard className="h-4 w-4" />
                  Account Number
                </div>
                <p className="text-base font-medium font-mono">
                  {bankAccount.maskedAccountNumber}
                </p>
              </div>

              {/* Bank Name */}
              <div className="space-y-1">
                <div className="flex items-center gap-2 text-sm font-medium text-gray-600">
                  <Building2 className="h-4 w-4" />
                  Bank Name
                </div>
                <p className="text-base font-medium">{bankAccount.bankName}</p>
              </div>

              {/* IFSC Code */}
              <div className="space-y-1">
                <div className="text-sm font-medium text-gray-600">IFSC Code</div>
                <p className="text-base font-medium font-mono">{bankAccount.ifscCode}</p>
              </div>

              {/* Branch Name */}
              {bankAccount.branchName && (
                <div className="space-y-1">
                  <div className="text-sm font-medium text-gray-600">Branch</div>
                  <p className="text-base font-medium">{bankAccount.branchName}</p>
                </div>
              )}

              {/* Account Type */}
              {bankAccount.accountType && (
                <div className="space-y-1">
                  <div className="text-sm font-medium text-gray-600">Account Type</div>
                  <p className="text-base font-medium">{bankAccount.accountType}</p>
                </div>
              )}
            </div>

            {/* Contact Information */}
            {(bankAccount.email || bankAccount.phone) && (
              <>
                <Separator />
                <div className="space-y-3">
                  <div className="text-sm font-medium text-gray-600">Contact Information</div>
                  {bankAccount.email && (
                    <div className="flex items-center gap-3">
                      <Mail className="h-4 w-4 text-gray-500" />
                      <span className="text-sm">{bankAccount.email}</span>
                    </div>
                  )}
                  {bankAccount.phone && (
                    <div className="flex items-center gap-3">
                      <Phone className="h-4 w-4 text-gray-500" />
                      <span className="text-sm">{bankAccount.phone}</span>
                    </div>
                  )}
                </div>
              </>
            )}

            {/* Timestamp */}
            <Separator />
            <div className="text-xs text-gray-500 space-y-1">
              <p>Created: {formatDate(bankAccount.createdAt)}</p>
              <p>Updated: {formatDate(bankAccount.updatedAt)}</p>
              {bankAccount.verifiedAt && (
                <p>Verified: {formatDate(bankAccount.verifiedAt)}</p>
              )}
            </div>

            {/* View and Edit Buttons */}
            {showEditButton && (
              <div className="flex gap-2">
                <Button
                  onClick={handleViewAccount}
                  variant="outline"
                  className="flex-1 gap-2"
                >
                  <Eye className="h-4 w-4" />
                  View
                </Button>
                <Button
                  onClick={handleEditAccount}
                  variant="outline"
                  className="flex-1 gap-2"
                >
                  <Edit2 className="h-4 w-4" />
                  Edit
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Bank Account Dialog */}
        <BankAccountDialog
          isOpen={dialogOpen}
          onOpenChange={setDialogOpen}
          mode={dialogMode}
          existingAccount={bankAccount}
          onSuccess={handleAccountSaved}
        />

        {/* View Bank Account Modal */}
        <Dialog open={viewModalOpen} onOpenChange={setViewModalOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Eye className="h-5 w-5" />
                Bank Account Details
              </DialogTitle>
              <DialogDescription>
                View your linked bank account information
              </DialogDescription>
            </DialogHeader>

            {bankAccount && (
              <div className="space-y-6">
                {/* Verification Status */}
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <span className="text-sm font-medium text-gray-700">Verification Status</span>
                  {bankAccount.isVerified ? (
                    <Badge className="bg-green-100 text-green-800 gap-1">
                      <CheckCircle className="h-3 w-3" />
                      Verified
                    </Badge>
                  ) : (
                    <Badge variant="secondary" className="gap-1">
                      <Clock className="h-3 w-3" />
                      Pending
                    </Badge>
                  )}
                </div>

                {/* Account Details Grid */}
                <div className="grid gap-4 md:grid-cols-2">
                  {/* Account Holder */}
                  <div className="space-y-1">
                    <div className="text-sm font-medium text-gray-600">Account Holder</div>
                    <p className="text-base font-medium">{bankAccount.accountHolderName}</p>
                  </div>

                  {/* Masked Account Number */}
                  <div className="space-y-1">
                    <div className="text-sm font-medium text-gray-600">Account Number</div>
                    <p className="text-base font-medium font-mono">{bankAccount.maskedAccountNumber}</p>
                  </div>

                  {/* Bank Name */}
                  <div className="space-y-1">
                    <div className="text-sm font-medium text-gray-600">Bank Name</div>
                    <p className="text-base font-medium">{bankAccount.bankName}</p>
                  </div>

                  {/* IFSC Code */}
                  <div className="space-y-1">
                    <div className="text-sm font-medium text-gray-600">IFSC Code</div>
                    <p className="text-base font-medium font-mono">{bankAccount.ifscCode}</p>
                  </div>

                  {/* Branch Name */}
                  {bankAccount.branchName && (
                    <div className="space-y-1">
                      <div className="text-sm font-medium text-gray-600">Branch</div>
                      <p className="text-base font-medium">{bankAccount.branchName}</p>
                    </div>
                  )}

                  {/* Account Type */}
                  {bankAccount.accountType && (
                    <div className="space-y-1">
                      <div className="text-sm font-medium text-gray-600">Account Type</div>
                      <p className="text-base font-medium">{bankAccount.accountType}</p>
                    </div>
                  )}
                </div>

                {/* Contact Information */}
                {(bankAccount.email || bankAccount.phone) && (
                  <>
                    <Separator />
                    <div className="space-y-3">
                      <div className="text-sm font-medium text-gray-600">Contact Information</div>
                      {bankAccount.email && (
                        <div className="flex items-center gap-3">
                          <Mail className="h-4 w-4 text-gray-500" />
                          <span className="text-sm">{bankAccount.email}</span>
                        </div>
                      )}
                      {bankAccount.phone && (
                        <div className="flex items-center gap-3">
                          <Phone className="h-4 w-4 text-gray-500" />
                          <span className="text-sm">{bankAccount.phone}</span>
                        </div>
                      )}
                    </div>
                  </>
                )}

                {/* Timestamps */}
                <Separator />
                <div className="text-xs text-gray-500 space-y-1">
                  <p>Created: {formatDate(bankAccount.createdAt)}</p>
                  <p>Updated: {formatDate(bankAccount.updatedAt)}</p>
                  {bankAccount.verifiedAt && (
                    <p>Verified: {formatDate(bankAccount.verifiedAt)}</p>
                  )}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </>
    );
  }

  return null;
};

export default BankAccountDisplay;
